import { useEffect, useState } from "react";
import { Avatar, Spin, Tag } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { clientEducationDetailApi } from "../../services/client-education-detail.api";
import type { EducationDetailDto } from "../../types/client-education-detail.types";
import sectionStyles from "./sections.module.scss";

interface Participant {
  id: string;
  userID: string;
  firstName?: string;
  lastName?: string;
  title?: string | null;
  department?: { id: string; name: string } | null;
}

interface ClassmatesSectionProps {
  education: EducationDetailDto;
  /** Kullanıcının kendi userID'si — listede "Sen" rozetiyle işaretlemek için. */
  currentUserId?: string;
}

/**
 * Eğitime kayıtlı sınıf arkadaşlarının listesini gösterir.
 * Her virtualClassroom için ayrı sorgu atılır; sonuçlar birleşip de-dup edilir.
 */
export function ClassmatesSection({
  education,
  currentUserId,
}: ClassmatesSectionProps) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const classrooms = education.virtualClassrooms ?? [];

  useEffect(() => {
    if (!classrooms.length) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      classrooms.map((c) =>
        clientEducationDetailApi.getClassroomParticipants(c.id),
      ),
    )
      .then((lists) => {
        if (cancelled) return;
        const merged = new Map<string, Participant>();
        lists.flat().forEach((p) => {
          if (!merged.has(p.userID)) merged.set(p.userID, p);
        });
        setParticipants(Array.from(merged.values()));
      })
      .catch(() => {
        if (!cancelled) setParticipants([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classrooms.map((c) => c.id).join(",")]);

  if (!classrooms.length) return null;

  return (
    <section id="classmates" className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <h2>
          Sınıf Arkadaşları{" "}
          {participants.length > 0 && (
            <Tag color="blue">{participants.length}</Tag>
          )}
        </h2>
      </div>

      {loading ? (
        <div style={{ padding: 16, textAlign: "center" }}>
          <Spin size="small" /> Yükleniyor…
        </div>
      ) : participants.length === 0 ? (
        <div style={{ padding: 16, color: "#94a3b8", textAlign: "center" }}>
          Bu sınıfta henüz başka kayıtlı kişi yok.
        </div>
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 10,
          }}
        >
          {participants.map((p) => {
            const fullName =
              `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || "—";
            const isMe = currentUserId && p.userID === currentUserId;
            return (
              <li
                key={p.userID || p.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                }}
              >
                <Avatar icon={<UserOutlined />} size={36} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13.5,
                      color: "#0f172a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fullName}
                    {isMe && (
                      <Tag color="green" style={{ marginLeft: 6 }}>
                        Sen
                      </Tag>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 11.5,
                      color: "#64748b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.title ?? ""}
                    {p.title && p.department?.name ? " · " : ""}
                    {p.department?.name ?? ""}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
